import {getProvider, getReportType, getScoreType} from '../helpers/creditHelper';

import {Dispatch} from 'redux';
import {buildCreditReportData} from '../helpers/buildCreditReportData';
import {buildCreditScoreData} from '../helpers/buildCreditScoreData';
import {getTrackingValues} from '../helpers/tracking';
import utils from '../helpers/utils';

/**
 * Helper function to safely access nested properties from window.CreditBundle
 * @param path - Array of property names representing the nested path (e.g., ['com', 'gendigital', 'credit', 'model', 'CreditModelJSWrapper'])
 * @param _name - Name of the property for error messages (e.g., 'CreditModelJSWrapper')
 * @returns A function that returns the nested property or throws an error if not available
 */
const getCreditBundleProperty = (path: string[], _name: string) => {
  return () => {
    let current: any = window.CreditBundle;
    const pathParts: string[] = [];
    for (const prop of path) {
      if (!current?.[prop]) {
        const currentPath = pathParts.length > 0 ? pathParts.join('.') : 'CreditBundle';
        throw new Error(`CreditBundle not loaded or ${currentPath}.${prop} not available`);
      }
      pathParts.push(prop);
      current = current[prop];
    }
    return current;
  };
};

export const CreditModelJSWrapper = getCreditBundleProperty(
  ['com', 'gendigital', 'credit', 'model', 'CreditModelJSWrapper'],
  'CreditModelJSWrapper'
);

export const CreditModel = getCreditBundleProperty(
  ['com', 'gendigital', 'credit', 'template', 'engine', 'model'],
  'CreditModel'
);

const CreditAppEnvironment = getCreditBundleProperty(
  ['com', 'gendigital', 'credit', 'network', 'client', 'utils', 'CreditAppEnvironment'],
  'CreditAppEnvironment'
);

export const CreditProvider = getCreditBundleProperty(
  ['com', 'gendigital', 'credit', 'model', 'CreditProvider'],
  'CreditProvider'
);

export const ViewStateHelpers = getCreditBundleProperty(
  ['com', 'gendigital', 'credit', 'model', 'ViewStateHelpers'],
  'ViewStateHelpers'
);

export const StateFlowCollector = getCreditBundleProperty(
  ['com', 'gendigital', 'credit', 'model', 'StateFlowCollector'],
  'StateFlowCollector'
);

export const StringDescJSHelper = getCreditBundleProperty(
  ['com', 'gendigital', 'credit', 'model', 'StringDescJSHelper'],
  'StringDescJSHelper'
);

export const MapJSHelper = getCreditBundleProperty(
  ['com', 'gendigital', 'credit', 'model', 'MapJSHelper'],
  'MapJSHelper'
);
//Common
export const SET_PROVIDER_BUREAU_DATA = 'SET_PROVIDER_BUREAU_DATA';

//Equifax
export const CREDIT_SCORE = 'CREDIT_SCORE';
export const CREDIT_SCORE_STATUS = 'CREDIT_SCORE_STATUS';

export const CREDIT_REPORT = 'CREDIT_REPORT';
export const CREDIT_REPORT_STATUS = 'CREDIT_REPORT_STATUS';

export const CREDIT_REPORT_ABSTRACT = 'CREDIT_REPORT_ABSTRACT';
export const CREDIT_REPORT_ABSTRACT_STATUS = 'CREDIT_REPORT_ABSTRACT_STATUS';

export const DOWNLOAD_REPORT_STATUS = 'DOWNLOAD_REPORT_STATUS';

export const CREDIT_FULFILLMENT_REQUEST = 'CREDIT_FULFILLMENT_REQUEST';
export const CREDIT_FULFILLMENT_SUCCESS = 'CREDIT_FULFILLMENT_SUCCESS';
export const CREDIT_FULFILLMENT_FAILURE = 'CREDIT_FULFILLMENT_FAILURE';
export const CREDIT_PULLINFO_REQUEST = 'CREDIT_PULLINFO_REQUEST';
export const CREDIT_PULLINFO_SUCCESS = 'CREDIT_PULLINFO_SUCCESS';
export const CREDIT_PULLINFO_FAILURE = 'CREDIT_PULLINFO_FAILURE';
export const CREDIT_ON_DEMAND = 'CREDIT_ON_DEMAND';
export const CLEAR_FULFILLMENT_INFO = 'CLEAR_FULFILLMENT_INFO';

export const INIT_CREDIT_SDK = 'INIT_CREDIT_SDK';
export const INIT_CREDIT_SDK_SUCCESS = 'INIT_CREDIT_SDK_SUCCESS';
export const INIT_CREDIT_SDK_FAILURE = 'INIT_CREDIT_SDK_FAILURE';

export function initCreditSdk() {
  return {
    type: INIT_CREDIT_SDK,
  };
}

//Common
export function setProviderBureauData(bureau: string, creditMax: $TSFixMe) {
  return {
    type: SET_PROVIDER_BUREAU_DATA,
    bureau,
    creditMax,
  };
}

// Latest Credit Score
/**
 * Fetches the latest credit score and history from the UnifiedCreditSDK.
 * @param dispatch - The Redux dispatch function to send actions
 * @param localizedStringBundle - A bundle of localized strings
 * @param langCode - The language code for localization (default is 'en').
 * @param accountId - hashed account id of the user
 * @param scoreType - The type of credit score to fetch (default is 'US_EFX'). It can be 'US_EFX', 'US_3B', 'CA_TU'.
 * @param provider - The provider to fetch the score from (default is 'EFX'). It can be 'EFX', 'CATU'.
 */

export async function getCreditScoreAndHistory({
  dispatch,
  localizedStringBundle,
  langCode,
  accountId,
  scoreType,
  provider,
}: {
  dispatch: Dispatch<any>;
  localizedStringBundle: StringMap;
  langCode: $TSFixMe;
  accountId: string;
  scoreType: string;
  provider: string;
}) {
  const startTime = Date.now();
  try {
    dispatch({type: 'CREDIT_SCORE_STATUS', credit_score_status: 'loading'});
    const traceId = getTrackingValues().NLokTraceId; // pass along trace id from NGP
    const resp = await CreditModelJSWrapper().getCreditScoreAndHistory(
      CreditModel().ScoreType[scoreType], // scoreType
      CreditModel().Provider[provider], // Provider
      traceId // traceId
    );
    const parsedResponse: GetCreditScoreAndHistoryResponse = JSON.parse(resp);
    const creditScoreData =
      parsedResponse.unifiedCreditScoreAndHistory?.length > 0
        ? parsedResponse.unifiedCreditScoreAndHistory
            .filter((item) => item.creditScore) // Only process items with creditScore
            .map((item) =>
              buildCreditScoreData(
                item.creditScore,
                item.scoreDelta,
                item.scoreHistory,
                localizedStringBundle,
                langCode,
                item.creditScore.provider
              )
            )
        : [];
    dispatch({type: 'CREDIT_SCORE', credit_score: creditScoreData});
    dispatch({type: 'CREDIT_SCORE_STATUS', credit_score_status: 'success'});
  } catch (error: $TSFixMe) {
    let errorStatus;
    try {
      const errorMessage = utils.tryParseJson(error?.message);
      const errorResponse = errorMessage ? errorMessage?.errorResponse : null;
      errorStatus = errorResponse?.serverErrorCode;
    } catch (parseError) {
      // console.log('Error message is not valid JSON', error?.message);
    }
    utils.log(buildCreditLogInfo(error, startTime, 'Get Credit Score and History', accountId, provider));
    if (errorStatus === 'CSR-API-4004') {
      return dispatch({type: 'CREDIT_SCORE_STATUS', credit_score_status: 'success', credit_score: []});
    }

    dispatch({type: 'CREDIT_SCORE_STATUS', credit_score_status: 'error', status_text: error.statusText || ''});
  }
}

/**
 * Fetches and processes the credit report data.
 *
 * This function retrieves the latest credit report and its history, processes the data,
 * and dispatches the appropriate actions to update the state. It also implements caching
 * to avoid redundant network requests for the same report IDs.
 *
 * @param clientLibraryInstance - The instance of the client library used to make API calls.
 * @param localizedStringBundle - A bundle of localized strings for formatting the credit report data.
 * @param langCode - The language code for localization (default is 'en').
 * @param accountId - The hashed account ID of the user.
 * @param reportType - The type of credit report to fetch (default is 'US_EFX'). It can be 'US_EFX', 'US_3B', 'CA_1B'.
 * @param provider - The provider to fetch the score from (default is 'EFX'). It can be 'EFX', 'CATU'.
 * @param country - The country of the user.
 *
 * @returns A thunk function that performs the asynchronous operations and dispatches actions.
 */
export async function getCreditReport({
  dispatch,
  localizedStringBundle,
  langCode = 'en',
  accountId,
  reportType,
  provider,
  country,
}: {
  dispatch: Dispatch<any>;
  localizedStringBundle: StringMap;
  langCode?: string;
  accountId: string;
  reportType: string;
  provider: string;
  country: string;
}) {
  const startTime = Date.now();

  try {
    const traceId = getTrackingValues().NLokTraceId; // pass along trace id from NGP
    // Indicate that the credit report fetching process has started
    dispatch({type: 'CREDIT_REPORT_STATUS', credit_report_status: 'loading'});

    const resp = await CreditModelJSWrapper().getCreditReportAndInsights(
      CreditModel().ReportType[reportType], // reportType
      CreditModel().Provider[provider], // Provider
      traceId // traceId
    );
    const parsedResponse: GetCreditReportAndInsightsResponse = JSON.parse(resp);

    const processedCreditReports = buildCreditReportData(
      parsedResponse,
      localizedStringBundle,
      langCode,
      provider,
      accountId,
      country
    );

    dispatch({type: 'CREDIT_REPORT_STATUS', credit_report_status: 'success'});
    dispatch({type: 'CREDIT_REPORT', credit_report: processedCreditReports});
    await getCreditReportList({dispatch, accountId, provider});
  } catch (error) {
    // Log the error information and dispatch an error status
    utils.log(buildCreditLogInfo(error, startTime, 'Get Credit Report And Insights', accountId, provider));
    dispatch({
      type: 'CREDIT_REPORT_STATUS',
      credit_report_status: 'error',
      credit_report_error: error,
      report_type: reportType,
    });
  }
}

// Download Credit Report
export function downloadCreditReport(reportid: string, accountId: string, bureau: string) {
  return async(dispatch: Dispatch<any>) => {
    const startTime = Date.now();
    try {
      dispatch({type: 'DOWNLOAD_REPORT_STATUS', download_report_status: 'loading'});
      const traceId = getTrackingValues().NLokTraceId; // pass along trace id from NGP

      const resp = await CreditModelJSWrapper().downloadReport(
        reportid,
        CreditModel().Provider[bureau], //provider
        traceId // traceId
      );
      dispatch({type: 'DOWNLOAD_REPORT_STATUS', download_report_status: 'success'});
      const downloadResponse: GetDownloadResponse = JSON.parse(resp);
      const url = utils.safeGet(downloadResponse, 'downloadResponse.downloadURL', '');
      const token = utils
        .safeGet(downloadResponse, 'downloadResponse.headers.Authorization', '')
        .substring('Bearer '.length);
      if (token === '' || url === '') {
        throw new Error('Url or Authorization token is missing in the download response');
      }
      const downloadFullUrl = url + '?access_token=' + token;
      return downloadFullUrl;
    } catch (error) {
      utils.log(buildCreditLogInfo(error, startTime, 'Download Credit Report', accountId, bureau));
      dispatch({type: 'DOWNLOAD_REPORT_STATUS', download_report_status: 'error', download_report_error: error});
    }
  };
}

// Credit Report History
export async function getCreditReportList({
  dispatch,
  accountId,
  provider,
}: {
  dispatch: Dispatch<any>;
  accountId: string;
  provider: string;
}) {
  const startTime = Date.now();
  try {
    dispatch({type: 'CREDIT_REPORT_ABSTRACT_STATUS', credit_report_abstract_status: 'loading'});
    const traceId = getTrackingValues().NLokTraceId; // pass along trace id from NGP

    const response = await CreditModelJSWrapper().getCreditReportList(
      CreditModel().Provider[provider], // Provider
      traceId // traceId
    );
    const parsedResponse: CreditReportAbstract[] = response ? JSON.parse(response).creditReports : [];
    dispatch({type: 'CREDIT_REPORT_ABSTRACT', credit_report_abstract: parsedResponse});
    dispatch({type: 'CREDIT_REPORT_ABSTRACT_STATUS', credit_report_abstract_status: 'success'});
    return parsedResponse;
  } catch (error) {
    utils.log(buildCreditLogInfo(error, startTime, 'Get Credit Report List', accountId, provider));
    dispatch({
      type: 'CREDIT_REPORT_ABSTRACT_STATUS',
      credit_report_abstract_status: 'error',
      credit_report_abstract_status_error: error,
    });
    return [null];
  }
}

// Define the new method
async function handleCreditScoreHistoryReport(
  dispatch: any,
  localizedStringBundle: any,
  langCode: string,
  is3BUser: boolean,
  accountId: string,
  bureau: string,
  country: string,
  creditPullInfo?: CreditPullInfo
) {
  const startTime = Date.now();

  try {
    const reportType = getReportType(bureau, is3BUser);
    const scoreType = getScoreType(bureau, is3BUser);
    const provider = getProvider(bureau);
    await getCreditScoreAndHistory({
      dispatch,
      localizedStringBundle,
      langCode,
      accountId,
      scoreType,
      provider,
    });
    if (bureau === 'TU' && creditPullInfo && !creditPullInfo.isFulfilled1BReportAvailable) {
      return;
    }
    await getCreditReport({
      dispatch,
      localizedStringBundle,
      langCode,
      accountId,
      reportType,
      provider,
      country,
    });
  } catch (error) {
    utils.log({
      feature: 'Credit',
      actionType: `handleCreditScoreHistoryReport client call failed`,
      message: error,
      statusCode: 401,
      // @ts-expect-error TS(2362) FIXME: The left-hand side of an arithmetic operation must... Remove this comment to see the full error message
      elapsedTime: new Date() - startTime,
      clientId: window.isDwm ? 'dwm' : 'dsp',
    });
  }
}

export function creditClient(
  localizedStringBundle: StringMap,
  langCode: string,
  is3BUser: boolean,
  accountId: string,
  bureau: string,
  country: string,
  creditPullInfo?: CreditPullInfo
) {
  return async(dispatch: $TSFixMe) => {
    handleCreditScoreHistoryReport(
      dispatch,
      localizedStringBundle,
      langCode,
      is3BUser,
      accountId,
      bureau,
      country,
      creditPullInfo
    );
  };
}

// Exported wrapper used solely by credit.test.js to mock creditClient when testing
// callCreditClient.
export const creditLib = {
  creditClient,
};

export const callCreditClient = (
  accountId: string,
  localizedStringBundle: StringMap,
  langCode: string,
  is3BUser: boolean,
  bureau: string,
  country: string,
  creditPullInfo?: CreditPullInfo
) => {
  return (dispatch: $TSFixMe) => {
    return dispatch(
      creditLib.creditClient(localizedStringBundle, langCode, is3BUser, accountId, bureau, country, creditPullInfo)
    );
  };
};

// Credit On Demand
export function setCreditOnDemand() {
  return {
    type: CREDIT_ON_DEMAND,
  };
}

// Credit Pull Info

function getCreditPullInfoRequest() {
  return {
    type: CREDIT_PULLINFO_REQUEST,
  };
}

function getCreditPullInfoFailure(error: $TSFixMe, options: $TSFixMe) {
  utils.log(buildCreditLogInfo(error, options.startTime, 'Get Credit Pull Info', options?.accountId));
  return {
    type: CREDIT_PULLINFO_FAILURE,
    error,
  };
}

function getCreditPullInfoSuccess(pullInfo: NextAvailablePullDatesResponse) {
  return {
    type: CREDIT_PULLINFO_SUCCESS,
    pullInfo: transformToCreditPullInfo(pullInfo),
  };
}

/**
 * Fetches credit pull information for a given account.
 * If the mock tool is enabled and mock endpoints are used, it fetches mock data.
 * Otherwise, it makes a real API call to fetch the data.
 *
 * @Param {string} accountId - The ID of the account to fetch credit pull information for.
 * @returns {Function} A thunk action that dispatches the appropriate actions based on the API response.
 */
export function getCreditPullInfo(accountId: string) {
  return async(dispatch: Dispatch<any>) => {
    try {
      dispatch(getCreditPullInfoRequest());
      const traceId = getTrackingValues().NLokTraceId; // pass along trace id from NGP

      const resp = await CreditModelJSWrapper().getNextPullDate(
        traceId // traceId
      );
      return dispatch(getCreditPullInfoSuccess(JSON.parse(resp)));
    } catch (error) {
      return dispatch(getCreditPullInfoFailure(error, {startTime: Date.now(), accountId}));
    }
  };
}

// Credit Fulfillment

function getFulfillmentRequest(refreshType: string, reportType: string) {
  return {
    type: CREDIT_FULFILLMENT_REQUEST,
    refreshType,
    reportType,
  };
}

function getFulfillmentSuccess(fulfillmentResponse: RefreshCreditScoreAndReportResponse) {
  return {
    type: CREDIT_FULFILLMENT_SUCCESS,
    fulfillmentInfo: fulfillmentResponse.refreshReportResponse,
  };
}

function getFulfillmentFailure(error: $TSFixMe, options: $TSFixMe) {
  utils.log(buildCreditLogInfo(error, options.startTime, 'Get Credit Fulfillment', options?.accountId));
  return {
    type: CREDIT_FULFILLMENT_FAILURE,
    error,
  };
}

export function getCreditFulfillment(refreshType: RefreshType, reportType: string, accountId: string) {
  return async(dispatch: Dispatch<any>) => {
    try {
      dispatch(getFulfillmentRequest(refreshType, reportType));
      const traceId = getTrackingValues().NLokTraceId; // pass along trace id from NGP
      const resp = await CreditModelJSWrapper().refreshCreditScoreAndReport(
        refreshType,
        CreditModel().ReportType[reportType],
        traceId // traceId
      );
      return dispatch(getFulfillmentSuccess(JSON.parse(resp)));
    } catch (error) {
      return dispatch(getFulfillmentFailure(error, {startTime: Date.now(), accountId}));
    }
  };
}

/**
 * Helper function to build up EFX error logging info
 *
 * @param error - The error object
 * @param startTime - The timestamp when the operation started
 * @param creditSdkInterface - The API creditSdkInterface or operation name
 * @param accountId - The user's account ID
 * @param bureau - The credit bureau (optional)
 * @returns {Object} Structured log info for error tracking
 */

function buildCreditLogInfo(
  error: any,
  startTime: number,
  creditSdkInterface: string,
  accountId: string,
  bureau?: string
) {
  const elapsedTime = Date.now() - startTime;
  return {
    feature: 'Credit',
    actionType: `${bureau} credit sdk call - ${creditSdkInterface}`,
    elapsedTime,
    error,
    clientId: window.isDwm ? 'dwm' : 'dsp',
    accountId,
  };
}

/**
 * Helper function to transform ProductFeatures from auth state to CreditProductFeatureDetails
 * @param productFeatures - ProductFeatures from auth state
 * @param auth - Auth state to extract additional properties
 * @returns CreditProductFeatureDetails object with plain string enum values
 *
 * Note: The SDK internally handles JSON serialization/deserialization to convert
 * string enum values to proper Kotlin enum instances.
 */
export function buildCreditProductFeatureDetails(
  productFeatures: ProductFeatures,
  auth: AuthState
): CreditProductFeatureDetails {
  const buildCreditFeature = (feature: any): CreditFeature | null => {
    if (!feature || !feature.featureStatus || !feature.fulfillmentStatus) {
      return null;
    }

    // Map frequencyUnitType from Days/Months/Years to DAILY/MONTHLY/ANNUAL (plain strings)
    let frequencyUnitType: FrequencyUnit | null = null;
    if (feature.frequencyUnitType) {
      const unit = feature.frequencyUnitType.toString().toLowerCase();
      if (unit === 'days' || unit === 'day') {
        frequencyUnitType = 'DAILY';
      } else if (unit === 'months' || unit === 'month') {
        frequencyUnitType = 'MONTHLY';
      } else if (unit === 'years' || unit === 'year') {
        frequencyUnitType = 'ANNUAL';
      } else if (['daily', 'monthly', 'annual'].includes(unit)) {
        frequencyUnitType = unit.toUpperCase() as FrequencyUnit;
      } else {
        frequencyUnitType = feature.frequencyUnitType as FrequencyUnit;
      }
    }

    // Return plain string values - SDK handles conversion to Kotlin enums internally
    return {
      featureStatus: feature.featureStatus as FeatureStatus,
      fulfillmentStatus: feature.fulfillmentStatus as FulfillmentStatus,
      frequencyUnitType: frequencyUnitType,
      frequencyUnitValue: feature.frequencyUnitValue ?? null,
      isOnDemand: feature.isOnDemand ?? null,
    };
  };

  const buildDeferredServicesSummary = (deferred: any): DeferredServicesSummary | null => {
    if (!deferred || typeof deferred.deferredEligible !== 'boolean') {
      return null;
    }
    return {
      deferredEligible: deferred.deferredEligible,
      deferredState: deferred.deferredState ? (deferred.deferredState as DeferredState) : null,
    };
  };

  const buildCreditFeaturesSummary = (summary: any): CreditFeaturesSummary | null => {
    if (!summary || typeof summary !== 'object') {
      return null;
    }

    // Validate required fields
    if (
      !summary.planType ||
      !summary.fulfillmentMode ||
      !Array.isArray(summary.bureauOrder) ||
      !summary.fulfilledBureau ||
      !summary.byBureaus ||
      typeof summary.byBureaus !== 'object'
    ) {
      return null;
    }

    // Validate planType and fulfillmentMode are valid CreditBureauMode values (1B, 2B, 3B)
    const validModes: CreditBureauMode[] = ['1B', '3B'];
    const planType = validModes.includes(summary.planType) ? summary.planType : null;
    const fulfillmentMode = validModes.includes(summary.fulfillmentMode) ? summary.fulfillmentMode : null;
    if (!planType || !fulfillmentMode) {
      return null;
    }

    // Validate bureauOrder contains only valid CreditBureau values
    const validBureaus: CreditBureau[] = ['EQUIFAX', 'TRANSUNION', 'EXPERIAN'];
    const bureauOrder = summary.bureauOrder.filter((bureau: string) =>
      validBureaus.includes(bureau as CreditBureau)
    ) as CreditBureau[];

    // Validate fulfilledBureau is a valid CreditBureau
    const fulfilledBureau = validBureaus.includes(summary.fulfilledBureau)
      ? (summary.fulfilledBureau as CreditBureau)
      : null;
    if (!fulfilledBureau) {
      return null;
    }

    // Build byBureaus object
    const byBureaus: Partial<Record<CreditBureau, CreditFeaturesSummaryBureauEntry[]>> = {};
    for (const bureauKey of validBureaus) {
      const bureauArray = summary.byBureaus[bureauKey];
      if (Array.isArray(bureauArray)) {
        const entries: CreditFeaturesSummaryBureauEntry[] = [];
        for (const entry of bureauArray) {
          if (!entry || typeof entry !== 'object') {
            continue;
          }

          // Validate bureauMode (1B, 2B, 3B)
          const bureauMode = validModes.includes(entry.bureauMode) ? entry.bureauMode : null;
          if (!bureauMode) {
            continue;
          }

          const bureauEntry: CreditFeaturesSummaryBureauEntry = {
            bureauMode,
            alerts: buildCreditFeature(entry.alerts),
            scores: buildCreditFeature(entry.scores),
            reports: buildCreditFeature(entry.reports),
          };

          // scoreHistory is available for all bureau modes
          if (entry.scoreHistory) {
            bureauEntry.scoreHistory = buildCreditFeature(entry.scoreHistory);
          }

          entries.push(bureauEntry);
        }
        if (entries.length > 0) {
          byBureaus[bureauKey] = entries;
        }
      }
    }

    return {
      planType,
      fulfillmentMode,
      bureauOrder,
      fulfilledBureau,
      byBureaus,
    };
  };

  const subscriptionCountry = utils.safeGet(auth, 'user.countryCode', null);
  const creditInsightsEnabled = true;

  return {
    creditScore1bCa: buildCreditFeature(productFeatures.creditScore1bCa),
    creditScoreEfx: buildCreditFeature(productFeatures.creditScore_1B), // Map creditScore_1B to creditScoreEfx
    creditScoreTu: buildCreditFeature(productFeatures.creditScore_3B), // derived from ProductFeatures
    creditScoreExp: buildCreditFeature(productFeatures.creditScore_3B), // derived from ProductFeatures
    creditScore3b: buildCreditFeature(productFeatures.creditScore_3B),
    creditScore1b: buildCreditFeature(productFeatures.creditScore_1B),
    creditReports1bCa: buildCreditFeature(productFeatures.creditReports1bCa),
    creditReportsEfx: buildCreditFeature(productFeatures.creditReports_1B), // Map creditReports_1B to creditReportsEfx
    creditReportsTu: buildCreditFeature(productFeatures.creditReports_3B), // derived from ProductFeatures
    creditReportsExp: buildCreditFeature(productFeatures.creditReports_3B), // derived from ProductFeatures
    creditReports3b: buildCreditFeature(productFeatures.creditReports_3B),
    creditReports1b: buildCreditFeature(productFeatures.creditReports_1B),
    creditViewUK: buildCreditFeature(productFeatures.creditView_1B_Gb),
    credit1bAu: buildCreditFeature((productFeatures as any).credit_1b_AU),
    creditMonitoring1b: buildCreditFeature(productFeatures.creditMonitoring_1B),

    deferredServicesSummary: buildDeferredServicesSummary(productFeatures.deferredServicesSummary),
    creditHistory: buildCreditFeature(productFeatures.creditHistory),
    creditHistoryCa: buildCreditFeature(productFeatures.creditHistoryCa),
    creditInsightsEnabled,
    subscriptionCountry,
    financialOffers: buildCreditFeature((productFeatures as any).financialOffers),
    creditFeaturesSummary: buildCreditFeaturesSummary((productFeatures as any).creditFeaturesSummary),
  };
}

export async function initializeCreditSDK({
  dispatch,
  accountId,
  auth,
}: {
  dispatch: Dispatch<any>;
  accountId: string;
  auth?: AuthState;
}) {
  const startTime = Date.now();
  try {
    dispatch(initCreditSdk());

    const creditEnv = CreditAppEnvironment();
    const envKey = window.CREDIT_ENV as string;
    const environment = creditEnv[envKey as keyof typeof creditEnv] || creditEnv.DEV;

    // Build CreditProductFeatureDetails from auth state
    let creditProductFeatureDetails: CreditProductFeatureDetails | undefined;

    const productFeatures = utils.getProductFeatures(auth) || {};
    creditProductFeatureDetails = buildCreditProductFeatureDetails(productFeatures, auth as AuthState);

    await CreditModelJSWrapper().initCreditSDK(
      'dsp',
      accountId,
      '',
      navigator.userAgent,
      environment,
      creditProductFeatureDetails
    );

    await StringDescJSHelper().preloadStringsSuspend();

    dispatch({type: INIT_CREDIT_SDK_SUCCESS});
  } catch (error: $TSFixMe) {
    utils.log(buildCreditLogInfo(error, startTime, 'Initialize credit sdk', accountId));
    dispatch({type: INIT_CREDIT_SDK_FAILURE});
  }
}

export function transformToCreditPullInfo(response: NextAvailablePullDatesResponse): CreditPullInfo {
  const result: CreditPullInfo = {
    isFulfilled1BReportAvailable: false,
    isFulfilled3BReportAvailable: false,
    isPull1BReportAllowed: false,
    isPull1BScoreAllowed: false,
    isPull3BReportAllowed: false,
    nextAvailable1BReportPullDate: 0,
    nextAvailable1BScorePullDate: 0,
    nextAvailable3BReportPullDate: 0,
  };

  // Iterate through all bureaus and their data arrays
  Object.values(response.nextAvailablePullDate).forEach((bureauArray) => {
    bureauArray.forEach((bureauData) => {
      if (bureauData.userType === 'US_EFX' || bureauData.userType === 'CA_1B') {
        result.isFulfilled1BReportAvailable = bureauData.fulfilledReportAvailable;
        result.isPull1BReportAllowed = bureauData.isPullReportAllowed;
        result.isPull1BScoreAllowed = bureauData.isPullScoreAllowed;
        result.nextAvailable1BReportPullDate = parseInt(bureauData.nextReportRefreshDate) || 0;
        result.nextAvailable1BScorePullDate = parseInt(bureauData.nextScoreRefreshDate) || 0;
      } else if (bureauData.userType === 'US_3B') {
        result.isFulfilled3BReportAvailable = bureauData.fulfilledReportAvailable;
        result.isPull3BReportAllowed = bureauData.isPullReportAllowed;
        result.nextAvailable3BReportPullDate = parseInt(bureauData.nextReportRefreshDate) || 0;
      }
    });
  });

  return result;
}

/*eslint-enable no-unused-vars*/
